import { useState } from "react";
import type { DeliverySettings, Zone } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { newId } from "../floor-admin/floor-edit";
import { amountText, kmText, readAmount, readKm } from "./zones";

interface Props {
  settings: DeliverySettings;
  /** null: a new zone. */
  zone: Zone | null;
  busy: boolean;
  onSave: (zone: Zone) => void;
  onRemove: (zone: Zone) => void;
  onClose: () => void;
}

/** "Nieuwe zone" / "Zone bewerken", as in the prototype, with every check of the prototype. */
export function ZoneDialog({ settings, zone, busy, onSave, onRemove, onClose }: Props) {
  const text = t.zonesAdmin.dialog;
  const [draft, setDraft] = useState(() => ({
    postcode: zone?.postcode ?? "",
    name: zone?.name ?? "",
    minimum: amountText(zone ? zone.minimumCents : 1500),
    fee: amountText(zone?.feeCents ?? null),
    freeFrom: amountText(zone?.freeFromCents ?? null),
    on: zone?.on ?? true,
    fromKm: kmText(zone?.far.fromKm ?? null),
    extra: amountText(zone?.far.extraCents ?? null),
    maxKm: kmText(zone?.far.maxKm ?? null),
  }));
  const [error, setError] = useState("");
  const set = (change: Partial<typeof draft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };

  const save = () => {
    const name = draft.name.trim();
    const postcode = draft.postcode.trim();
    const minimumCents = readAmount(draft.minimum);
    const feeCents = readAmount(draft.fee);
    const freeFromCents = readAmount(draft.freeFrom);
    const fromKm = readKm(draft.fromKm);
    const extraCents = readAmount(draft.extra);
    const maxKm = readKm(draft.maxKm);
    const problem = !/^\d{4}$/.test(postcode)
      ? text.badPostcode
      : !name
        ? text.noZone
        : // the same name in another postcode is another zone (Puurs-Sint-Amands 2870 and 2890)
          settings.zones.some(
              (other) =>
                other.id !== zone?.id && other.postcode === postcode && other.name.toLowerCase() === name.toLowerCase(),
            )
          ? text.exists
          : minimumCents === null || minimumCents === undefined
            ? text.noMinimum
            : feeCents === undefined || freeFromCents === undefined || extraCents === undefined
              ? text.badAmount
              : fromKm === undefined || maxKm === undefined || (fromKm === null) !== (extraCents === null)
                ? text.farIncomplete
                : maxKm !== null && fromKm !== null && maxKm <= fromKm
                  ? text.farMax
                  : "";
    if (problem) {
      setError(problem);
      return;
    }
    onSave({
      id: zone?.id ?? newId(),
      postcode,
      name,
      minimumCents: minimumCents!,
      feeCents: feeCents ?? null,
      freeFromCents: freeFromCents ?? null,
      on: draft.on,
      far: { fromKm: fromKm ?? null, extraCents: extraCents ?? null, maxKm: maxKm ?? null },
    });
  };

  const field = (label: string, key: keyof typeof draft, note?: string, numeric?: "numeric" | "decimal") => (
    <label>
      <span>
        {label} {note && <small className="note">{note}</small>}
      </span>
      <input
        value={String(draft[key])}
        className={numeric ? "num" : undefined}
        inputMode={numeric}
        maxLength={key === "postcode" ? 4 : 60}
        autoComplete="off"
        data-autofocus={(zone ? key === "minimum" : key === "postcode") ? true : undefined}
        onChange={(event) => {
          set({ [key]: event.target.value });
        }}
      />
    </label>
  );
  const small = (key: "fromKm" | "extra" | "maxKm", label: string) => (
    <input
      className="num"
      inputMode="decimal"
      aria-label={label}
      value={draft[key]}
      onChange={(event) => {
        set({ [key]: event.target.value });
      }}
    />
  );

  return (
    <Dialog
      title={zone ? text.edit : text.new}
      size="wide"
      onClose={onClose}
      footer={
        <>
          {zone && (
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              onClick={() => {
                onRemove(zone);
              }}
            >
              {text.remove}
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {text.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={save}>
            {text.save}
          </button>
        </>
      }
    >
      <div className="sfrm">
        {field(text.postcode, "postcode", undefined, "numeric")}
        {field(text.zone, "name")}
        {field(text.minimum, "minimum", undefined, "decimal")}
        {field(text.fee, "fee", text.standard(formatEuro(settings.feeCents)), "decimal")}
        {field(
          text.freeFrom,
          "freeFrom",
          text.standard(settings.freeFromCents === null ? t.zonesAdmin.never : formatEuro(settings.freeFromCents)),
          "decimal",
        )}
        <label className="inl">
          <input
            type="checkbox"
            checked={draft.on}
            onChange={(event) => {
              set({ on: event.target.checked });
            }}
          />{" "}
          {text.on}
        </label>
      </div>
      <div className="zbox">
        <h3>
          {text.far} <small className="note">{text.optional}</small>
        </h3>
        <div className="inl zinl">
          {text.fromKm} {small("fromKm", text.fromKmLabel)} {text.kmFromShop} {small("extra", text.extraLabel)}
        </div>
        <div className="inl zinl">
          {text.maxKm} {small("maxKm", text.maxLabel)} {text.km}
        </div>
        <p className="note">{text.farNote}</p>
      </div>
      <p className="pinmsg" role="alert">
        {error}
      </p>
    </Dialog>
  );
}
